import { parseInt } from "lodash";
import { IHttpClient } from "../../../lib/http";
import {
  AccommodationSortingCriteriaMapping,
  AccommodationSortingCriteriaReverseMapping,
  BehaviourDto,
  BehaviourEntity,
  BundlingConfiguration,
  DepositCheckoutTextConfiguration,
  DepositNotRequiredTextConfiguration,
  DepositRequiredTextConfiguration,
  ExtrasTextConfiguration,
  InventoryGroupingCriteria,
  InventoryGroupingCriteriaDtoReverseMapping,
  InventoryGroupingCriteriaMapping,
  OccupationAlternatives,
  PendingAmountTextConfiguration,
  PriceCalendarMapping,
  PriceCalendarReverseMapping,
  PromosConfig,
  RateType,
  RoomSelectionViewType,
  ShowExtrasConfigurationMapping,
  ShowExtrasConfigurationReverseMapping,
  ShowPromoCodeCriteria,
  SplitStay,
  StringBoolean,
  StringBooleanMapping,
  TaxesText,
} from "../domain/behaviour.entity";

function toStringBoolean(prop: boolean) {
  return StringBooleanMapping[prop ? "true" : "false"];
}

function toBoolean(prop: StringBoolean | boolean) {
  if (typeof prop == "boolean") {
    return prop;
  }
  return prop === "1";
}

export const maxAccommodationsMultiReservationDefault = "5";

function entityToDto(entity: BehaviourEntity): BehaviourDto {
  return {
    accommodationsOrder: AccommodationSortingCriteriaMapping[entity.accommodationsOrder],
    addObligatoryExtrasRateToRoomSelection: toStringBoolean(
      entity.addObligatoryExtrasRateToRoomSelection
    ),
    showDecimalsInExtrasPage: toStringBoolean(entity.showDecimalsInExtrasPage),
    isCancellationReasonRequired: toStringBoolean(entity.isCancellationReasonRequired),
    depositNotRequiredTextConfiguration: entity.depositNotRequiredTextConfiguration,
    depositRequiredTextConfiguration: entity.depositRequiredTextConfiguration,
    hasRoomCombination: toStringBoolean(entity.hasRoomCombination),
    hideObligatoryExtras: toStringBoolean(entity.hideObligatoryExtras),
    inventoryGroupingCriteria: InventoryGroupingCriteriaMapping[entity.inventoryGroupingCriteria],
    maxHabitacionesParaOcultarSoloQuedan: "" + entity.roomsLeftAvailabilityLimit,
    maxHabitacionesFront: `${entity.maxRoomsInUnitSelector}`,
    ocultarBloqueadas: toStringBoolean(entity.hideLocked),
    ocultarRestringuidas: toStringBoolean(entity.hideRestricted),
    pendingAmountTextConfiguration: entity.pendingAmountTextConfiguration,
    priceCalendar: PriceCalendarMapping[entity.priceCalendar],
    promoCode: entity.showPromoCodeCriteria,
    roomSelectionViewType: entity.roomSelectionViewType,
    servicesCollapsed: ShowExtrasConfigurationMapping[entity.showExtrasConfiguration],
    showAllReservationsIds: toStringBoolean(entity.showAllReservationsIds),
    showBestPriceGuaranteed: toStringBoolean(entity.showBestPriceGuaranteed),
    showBookingForm: toStringBoolean(entity.showBookingForm),
    showCancelMsg: toStringBoolean(entity.showCancelMsg),
    showCookiePolicy: toStringBoolean(entity.showCookiePolicy),
    showCurrencySelector: toStringBoolean(entity.showCurrencySelector),
    showDesktopLogo: toStringBoolean(entity.showDesktopLogo),
    showDiscountsCalculation: toStringBoolean(entity.showDiscountsCalculation),
    showFullReservationIds: toStringBoolean(entity.showFullReservationIds),
    showInactiveDiscounts: toStringBoolean(
      entity.showInactiveDiscounts === PromosConfig.SHOW_ALWAYS
    ),
    showInventoryPriceDiff: toStringBoolean(entity.showInventoryPriceDiff),
    showLanguageSelector: toStringBoolean(entity.showLanguageSelector),
    showMobileLogo: toStringBoolean(entity.showMobileLogo),
    showOccupationAlternatives: entity.showOccupationAlternatives,
    splitStayConfiguration: entity.splitStayConfiguration,
    showPromos: toStringBoolean(entity.showPromos),
    showRestrictedHighlightMessages: toStringBoolean(
      entity.showRestrictedHighlightMessages === PromosConfig.SHOW_ALWAYS
    ),
    tachaPrecios: toStringBoolean(entity.showRackRate),
    taxesText: entity.taxesText,
    unavailableURL: entity.unavailableURL ?? null,
    logolinkstohotel: toStringBoolean(entity.isLogoLinkingToPropertyUrl),
    urlwebhotel: entity.propertyUrl,
    witbookerPageTitle: entity.enginePageTitle,
    bundlingConfiguration: toStringBoolean(
      entity.bundlingConfiguration !== BundlingConfiguration.HIDE
    ),
    bundlingAvailability: toStringBoolean(
      entity.bundlingConfiguration === BundlingConfiguration.SHOW_AVAIL
    ),
    waitingListEnabled: toStringBoolean(entity.waitingListEnabled),
    showCheckoutButtonRoomUnits: toStringBoolean(entity.showCheckoutButtonRoomUnits),
    allowMultiplyPriceByUnits: toStringBoolean(entity.allowMultiplyPriceByUnits),
    depositCheckoutTextConfiguration: entity.depositCheckoutTextConfiguration,
    extrasTextConfiguration: entity.extrasTextConfiguration,
    showMultipleAccommodations: toStringBoolean(
      !!entity.maxAccommodationsMultiReservationSection?.showMultipleAccommodations
    ),
    maxAccommodationsMultiReservation: entity.maxAccommodationsMultiReservationSection
      ?.maxAccommodationsMultiReservation
      ? entity.maxAccommodationsMultiReservationSection.maxAccommodationsMultiReservation.toString()
      : maxAccommodationsMultiReservationDefault.toString(),
    taxesRateType: entity.taxesRateType,
    overrideCurrencyAccordingToLocation: toStringBoolean(
      entity.overrideCurrencyAccordingToLocation
    ),
  };
}

// we do not need the palette inside the engineAppearance to have a distinct UUID
const baseUUID = "3639076e-8f86-442f-b1c6-d123b718b9c7";

function parseIntOrDefault(val: string, number: number) {
  return parseInt(val) || number;
}

function dtoToEntity(dto: BehaviourDto): BehaviourEntity {
  return {
    id: baseUUID,
    accommodationsOrder: AccommodationSortingCriteriaReverseMapping[dto.accommodationsOrder],
    addObligatoryExtrasRateToRoomSelection: toBoolean(dto.addObligatoryExtrasRateToRoomSelection),
    showDecimalsInExtrasPage: toBoolean(dto.showDecimalsInExtrasPage),
    isCancellationReasonRequired: toBoolean(dto.isCancellationReasonRequired),
    depositNotRequiredTextConfiguration: dto.depositNotRequiredTextConfiguration,
    depositRequiredTextConfiguration: dto.depositRequiredTextConfiguration,
    hasRoomCombination: toBoolean(dto.hasRoomCombination),
    hideObligatoryExtras: toBoolean(dto.hideObligatoryExtras),
    inventoryGroupingCriteria: dto.inventoryGroupingCriteria
      ? InventoryGroupingCriteriaDtoReverseMapping[dto.inventoryGroupingCriteria]
      : InventoryGroupingCriteria.NONE,
    roomsLeftAvailabilityLimit: dto.maxHabitacionesParaOcultarSoloQuedan
      ? parseIntOrDefault(dto.maxHabitacionesParaOcultarSoloQuedan, 0)
      : 0,
    maxRoomsInUnitSelector: dto.maxHabitacionesFront
      ? parseIntOrDefault(dto.maxHabitacionesFront, 10)
      : 10,
    hideLocked: toBoolean(dto.ocultarBloqueadas),
    hideRestricted: toBoolean(dto.ocultarRestringuidas),
    pendingAmountTextConfiguration: dto.pendingAmountTextConfiguration,
    priceCalendar: PriceCalendarReverseMapping[dto.priceCalendar],
    showPromoCodeCriteria: dto.promoCode,
    roomSelectionViewType: dto.roomSelectionViewType,
    showExtrasConfiguration: ShowExtrasConfigurationReverseMapping[dto.servicesCollapsed],
    showAllReservationsIds: toBoolean(dto.showAllReservationsIds),
    showBestPriceGuaranteed: toBoolean(dto.showBestPriceGuaranteed),
    showBookingForm: toBoolean(dto.showBookingForm),
    showCancelMsg: toBoolean(dto.showCancelMsg),
    showCookiePolicy: toBoolean(dto.showCookiePolicy),
    showCurrencySelector: toBoolean(dto.showCurrencySelector),
    showDesktopLogo: toBoolean(dto.showDesktopLogo),
    showDiscountsCalculation: toBoolean(dto.showDiscountsCalculation),
    showFullReservationIds: toBoolean(dto.showFullReservationIds),
    showInactiveDiscounts: toBoolean(dto.showInactiveDiscounts)
      ? PromosConfig.SHOW_ALWAYS
      : PromosConfig.SHOW_WHEN_APPLICABLE,
    showInventoryPriceDiff: toBoolean(dto.showInventoryPriceDiff),
    showLanguageSelector: toBoolean(dto.showLanguageSelector),
    showMobileLogo: toBoolean(dto.showMobileLogo),
    showOccupationAlternatives: dto.showOccupationAlternatives,
    splitStayConfiguration: dto.splitStayConfiguration,
    showPromos: toBoolean(dto.showPromos),
    showRestrictedHighlightMessages: toBoolean(dto.showRestrictedHighlightMessages)
      ? PromosConfig.SHOW_ALWAYS
      : PromosConfig.SHOW_WHEN_APPLICABLE,
    showRackRate: toBoolean(dto.tachaPrecios),
    taxesText: dto.taxesText,
    unavailableURL: dto.unavailableURL,
    enginePageTitle: dto.witbookerPageTitle,
    isLogoLinkingToPropertyUrl: toBoolean(dto.logolinkstohotel),
    propertyUrl: dto.urlwebhotel,
    bundlingConfiguration: toBoolean(dto.bundlingConfiguration)
      ? toBoolean(dto.bundlingAvailability)
        ? BundlingConfiguration.SHOW_AVAIL
        : BundlingConfiguration.SHOW
      : BundlingConfiguration.HIDE,
    waitingListEnabled: toBoolean(dto.waitingListEnabled),
    showCheckoutButtonRoomUnits: toBoolean(dto.showCheckoutButtonRoomUnits),
    allowMultiplyPriceByUnits: toBoolean(dto.allowMultiplyPriceByUnits),
    depositCheckoutTextConfiguration: dto.depositCheckoutTextConfiguration,
    extrasTextConfiguration: dto.extrasTextConfiguration,
    maxAccommodationsMultiReservation: dto.maxAccommodationsMultiReservation,
    showMultipleAccommodations: toBoolean(dto.showMultipleAccommodations),
    maxAccommodationsMultiReservationSection: {
      showMultipleAccommodations: toBoolean(dto.showMultipleAccommodations),
      maxAccommodationsMultiReservation:
        Number.parseInt(dto.maxAccommodationsMultiReservation) || undefined,
    },
    taxesRateType: dto.taxesRateType,
    overrideCurrencyAccordingToLocation: toBoolean(dto.overrideCurrencyAccordingToLocation),
  };
}

function buildUrlParams(): URLSearchParams {
  const dto: BehaviourDto = {
    accommodationsOrder: "listPosition",
    addObligatoryExtrasRateToRoomSelection: "1",
    showDecimalsInExtrasPage: "0",
    isCancellationReasonRequired: "0",
    depositNotRequiredTextConfiguration: DepositNotRequiredTextConfiguration.DEPOSIT_NOT_REQUIRED,
    depositRequiredTextConfiguration: DepositRequiredTextConfiguration.DEPOSIT_REQUIRED,
    hasRoomCombination: "0",
    hideObligatoryExtras: "0",
    inventoryGroupingCriteria: "",
    maxHabitacionesParaOcultarSoloQuedan: "0",
    maxHabitacionesFront: "0",
    ocultarBloqueadas: "0",
    ocultarRestringuidas: "0",
    pendingAmountTextConfiguration: PendingAmountTextConfiguration.PAY_AT_ESTABLISHMENT,
    priceCalendar: "showAllPrices",
    promoCode: ShowPromoCodeCriteria.HIDE,
    roomSelectionViewType: RoomSelectionViewType.GRID,
    servicesCollapsed: "collapsed",
    showAllReservationsIds: "0",
    showBestPriceGuaranteed: "0",
    showBookingForm: "0",
    showCancelMsg: "0",
    showCookiePolicy: "0",
    showCurrencySelector: "0",
    showDesktopLogo: "0",
    showDiscountsCalculation: "0",
    showFullReservationIds: "0",
    showInactiveDiscounts: "0",
    showInventoryPriceDiff: "0",
    showLanguageSelector: "0",
    showMobileLogo: "0",
    showMultipleAccommodations: "0",
    showOccupationAlternatives: OccupationAlternatives.HIDE,
    splitStayConfiguration: SplitStay.NONE,
    showPromos: "0",
    showRestrictedHighlightMessages: "0",
    tachaPrecios: "0",
    taxesText: TaxesText.TAXES_INCLUDED,
    unavailableURL: "",
    logolinkstohotel: "0",
    urlwebhotel: "",
    witbookerPageTitle: "",
    bundlingConfiguration: "0",
    bundlingAvailability: "0",
    waitingListEnabled: "0",
    showCheckoutButtonRoomUnits: "0",
    allowMultiplyPriceByUnits: "0",
    depositCheckoutTextConfiguration: DepositCheckoutTextConfiguration.DEPOSIT,
    extrasTextConfiguration: ExtrasTextConfiguration.EXTRAS,
    maxAccommodationsMultiReservation: maxAccommodationsMultiReservationDefault,
    taxesRateType: RateType.INCLUDED,
    overrideCurrencyAccordingToLocation: "0",
  };
  const params = new URLSearchParams();
  Object.keys(dto).forEach(k => params.append("prop", k));
  return params;
}

export async function getBehaviour(
  httpClient: IHttpClient,
  propertyTicker: string
): Promise<BehaviourEntity> {
  const params: URLSearchParams = buildUrlParams();
  const behaviourDto: BehaviourDto = await httpClient.get<BehaviourDto>(
    `/WitBookerAPI-war/webresources/endpoint/establishment/${propertyTicker}/configurations`,
    { params }
  );
  return dtoToEntity(behaviourDto);
}

export async function updateBehaviour(
  propertyTicker: string,
  entity: BehaviourEntity,
  httpClient: IHttpClient
): Promise<void> {
  await httpClient.post(
    `/WitBookerAPI-war/webresources/endpoint/establishment/${propertyTicker}/createOrUpdateConfigurations`,
    entityToDto(entity)
  );
}
